import { dataRepository } from '../repository/DataRepository';
import { notificationService } from './NotificationService';
import { eventBus } from '../events/EventBus';

const SETTINGS_KEY = 'pkm-notification-settings';
const COMPLETED_STATUSES = new Set(['done', 'completed', 'cancelled']);

function getSettings(): { enabled: boolean; minutes: number } {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return {
        enabled: parsed.taskRemindersEnabled !== false,
        minutes: parsed.taskReminderMinutes ?? 30,
      };
    }
  } catch { /* use defaults */ }
  return { enabled: true, minutes: 30 };
}

async function reconcileTaskNotifications(): Promise<void> {
  const { enabled, minutes } = getSettings();

  if (!enabled) {
    notificationService.cancelAllTaskReminders();
    return;
  }

  if (!notificationService.isEnabled()) return;

  const tasks = await dataRepository.getObjectsByType('task');
  const now = new Date();
  const seen = new Set<string>();

  for (const task of tasks) {
    const props = await dataRepository.getPropertiesForObject(task.id);
    const dueDateVal = props.find(p => p.name === 'due_date')?.value;
    if (!dueDateVal) continue;

    const status = props.find(p => p.name === 'status')?.value?.toLowerCase();
    if (status && COMPLETED_STATUSES.has(status)) continue;

    const timePart = props.find(p => p.name === 'due_time')?.value || '09:00';
    const dueDateTime = new Date(`${dueDateVal}T${timePart}:00`);
    if (isNaN(dueDateTime.getTime()) || dueDateTime <= now) continue;

    const notifId = `task-${task.id}`;
    seen.add(notifId);

    const reminderTime = new Date(dueDateTime.getTime() - minutes * 60 * 1000);
    const expectedFireTime = reminderTime <= now ? dueDateTime : reminderTime;
    const existing = notificationService.getScheduledFireTime(notifId);

    if (existing && Math.abs(existing.getTime() - expectedFireTime.getTime()) < 1000) {
      continue;
    }

    notificationService.scheduleTaskReminder(task.id, task.name, dueDateTime, minutes);
  }

  // Cancel reminders for tasks that no longer qualify
  for (const n of notificationService.getScheduledNotifications()) {
    if (n.id.startsWith('task-') && !seen.has(n.id)) {
      notificationService.cancelNotification(n.id);
    }
  }
}

let unsubSync: (() => void) | null = null;

export function startTaskNotificationReconciler(): void {
  reconcileTaskNotifications();
  unsubSync = eventBus.on('sync:completed', () => reconcileTaskNotifications());
}

export function stopTaskNotificationReconciler(): void {
  unsubSync?.();
  unsubSync = null;
}
